/**
 * High-level Concord operations: founding the community, accepting invites,
 * chat-plane actions, and Control Plane writes. Each function builds the
 * exact wire events the CORDs specify and hands them to a relay sink.
 */

import { getEventHash } from "nostr-tools";

import {
  COMMUNITY_DEFAULT_DESCRIPTION,
  COMMUNITY_DEFAULT_NAME,
  COMMUNITY_RELAYS,
  KIND_CHAT_MESSAGE,
  KIND_DELETE,
  KIND_EDIT,
  KIND_EDITION,
  KIND_JOIN_LEAVE,
  KIND_REACTION,
  KIND_SEAL_ENCRYPTED,
  KIND_SEAL_PLAINTEXT,
} from "./config";
import {
  banlistLocator,
  bytesToHex,
  channelKey,
  communityId,
  controlReadKey,
  controlSignerKey,
  guestbookKey,
  hexToBytes,
  randomBytes,
} from "./crypto";
import { VSK, editionHash, type Edition } from "./editions";
import { concordTime, createStreamEvent, type GiftSigner } from "./stream";
import type { EventSink } from "./membership";
import type { InviteBundle, Membership } from "./types";

export interface GenesisResult {
  membership: Membership;
  generalChannelId: string;
}

/**
 * Found a new community (CORD-02 §1): mint the three secrets, publish the two
 * genesis editions (community metadata + the `#general` channel), and return
 * the membership material the owner stores in their Community List.
 */
export async function foundCommunity(opts: {
  signer: GiftSigner;
  ownerPub: string;
  name?: string;
  description?: string;
  sink: EventSink;
}): Promise<GenesisResult> {
  const { signer, ownerPub, sink } = opts;
  const owner_salt = bytesToHex(randomBytes(32));
  const community_root = bytesToHex(randomBytes(32));
  const control_root = bytesToHex(randomBytes(32));
  const community_id = await communityId(ownerPub, hexToBytes(owner_salt));
  const root_epoch = 0;

  const controlSigner = await controlSignerKey(control_root, community_id, root_epoch);
  const controlRead = await controlReadKey(community_root, community_id, root_epoch);
  // The Control Plane's split key (CORD-02 §5): wraps are signed by the
  // staff-held signer key but encrypted under the member-held read key.
  const controlStream = { ...controlSigner, convKey: controlRead.convKey };
  const generalChannelId = bytesToHex(randomBytes(32));

  const publishEdition = async (vsk: number, eid: string, content: unknown) => {
    const { created_at, ms } = concordTime();
    const wrap = await createStreamEvent({
      stream: controlStream,
      sealKind: KIND_SEAL_PLAINTEXT,
      authorPub: ownerPub,
      signer,
      rumor: {
        kind: KIND_EDITION,
        content: JSON.stringify(content),
        tags: [
          ["vsk", String(vsk)],
          ["eid", eid],
          ["ev", "1"],
          ["ms", ms],
        ],
        created_at,
      },
    });
    await sink.event(wrap);
  };

  // Genesis is exactly two owner-signed editions (CORD-02 §1).
  await publishEdition(VSK.METADATA, community_id, {
    name: opts.name ?? COMMUNITY_DEFAULT_NAME,
    description: opts.description ?? COMMUNITY_DEFAULT_DESCRIPTION,
    relays: COMMUNITY_RELAYS,
  });
  await publishEdition(VSK.CHANNEL, generalChannelId, {
    name: "general",
    private: false,
  });

  const membership: Membership = {
    communityId: community_id,
    addedAt: Date.now(),
    current: {
      owner: ownerPub,
      owner_salt,
      community_root,
      root_epoch,
      control_pk: controlSigner.pk,
      channels: [],
      relays: COMMUNITY_RELAYS,
      name: opts.name ?? COMMUNITY_DEFAULT_NAME,
      control_root,
    },
  };
  return { membership, generalChannelId };
}

/** The membership an invitee stores after accepting a Direct Invite. */
export function membershipFromBundle(bundle: InviteBundle): Membership {
  return {
    communityId: bundle.community_id,
    addedAt: Date.now(),
    current: {
      owner: bundle.owner,
      owner_salt: bundle.owner_salt,
      community_root: bundle.community_root,
      root_epoch: bundle.root_epoch,
      control_pk: bundle.control_pk ?? "",
      channels: bundle.channels,
      relays: bundle.relays,
      name: bundle.name,
    },
  };
}

/** Announce membership on the Guestbook plane (CORD-02 §5). */
export async function publishGuestbookEntry(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  verb: "join" | "leave";
  sink: EventSink;
}): Promise<void> {
  const { signer, authorPub, membership, verb, sink } = opts;
  const m = membership.current;
  const gb = await guestbookKey(m.community_root, membership.communityId, m.root_epoch);
  const { created_at, ms } = concordTime();
  const tags = [["ms", ms]];
  if (verb === "join") {
    tags.push(["invite", m.owner, "Chama Chat"]);
  }
  const wrap = await createStreamEvent({
    stream: gb,
    sealKind: KIND_SEAL_ENCRYPTED,
    authorPub,
    signer,
    rumor: { kind: KIND_JOIN_LEAVE, content: verb, tags, created_at },
  });
  await sink.event(wrap);
}

// ---------------------------------------------------------------------------
// Chat plane actions (CORD-03 §3)
// ---------------------------------------------------------------------------

async function publishChatRumor(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  channelId: string;
  kind: number;
  content: string;
  extraTags: string[][];
  sink: EventSink;
}): Promise<{ id: string; createdAtMs: number; wrap: import("@nostrify/nostrify").NostrEvent }> {
  const { signer, authorPub, membership, channelId, kind, content, extraTags, sink } = opts;
  const m = membership.current;
  const stream = await channelKey(m.community_root, channelId, m.root_epoch);
  const { created_at, ms } = concordTime();
  const tags = [
    ["channel", channelId],
    ["epoch", String(m.root_epoch)],
    ["ms", ms],
    ...extraTags,
  ];
  const wrap = await createStreamEvent({
    stream,
    sealKind: KIND_SEAL_ENCRYPTED,
    authorPub,
    signer,
    rumor: { kind, content, tags, created_at },
  });
  await sink.event(wrap);
  const id = getEventHash({ kind, pubkey: authorPub, created_at, tags, content });
  return { id, createdAtMs: created_at * 1000 + Number(ms), wrap };
}

export function publishChatMessage(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  channelId: string;
  content: string;
  sink: EventSink;
}) {
  return publishChatRumor({ ...opts, kind: KIND_CHAT_MESSAGE, extraTags: [] });
}

export function publishReaction(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  channelId: string;
  targetId: string;
  targetPub: string;
  emoji: string;
  sink: EventSink;
}) {
  const { targetId, targetPub, emoji, ...rest } = opts;
  return publishChatRumor({
    ...rest,
    kind: KIND_REACTION,
    content: emoji,
    extraTags: [
      ["e", targetId],
      ["p", targetPub],
      ["k", String(KIND_CHAT_MESSAGE)],
    ],
  });
}

export function publishDeleteMessage(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  channelId: string;
  targetId: string;
  sink: EventSink;
}) {
  const { targetId, ...rest } = opts;
  return publishChatRumor({
    ...rest,
    kind: KIND_DELETE,
    content: "",
    extraTags: [
      ["e", targetId],
      ["k", String(KIND_CHAT_MESSAGE)],
    ],
  });
}

export function publishEditMessage(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  channelId: string;
  targetId: string;
  content: string;
  sink: EventSink;
}) {
  const { targetId, content, ...rest } = opts;
  return publishChatRumor({
    ...rest,
    kind: KIND_EDIT,
    content,
    extraTags: [["e", targetId]],
  });
}

// ---------------------------------------------------------------------------
// Control Plane writes (owner/staff)
// ---------------------------------------------------------------------------

async function nextEditionTags(opts: {
  vsk: number;
  eid: string;
  heads: Map<string, Edition>;
}): Promise<string[][]> {
  const key = `${opts.vsk}:${opts.eid}`;
  const prev = opts.heads.get(key);
  if (!prev) {
    return [
      ["vsk", String(opts.vsk)],
      ["eid", opts.eid],
      ["ev", "1"],
    ];
  }
  const ep = await editionHash(prev);
  return [
    ["vsk", String(opts.vsk)],
    ["eid", opts.eid],
    ["ev", String(prev.ev + 1)],
    ["ep", ep],
  ];
}

async function publishEdition(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  vsk: number;
  eid: string;
  content: unknown;
  heads: Map<string, Edition>;
  sink: EventSink;
}): Promise<void> {
  const { signer, authorPub, membership, vsk, eid, content, heads, sink } = opts;
  const m = membership.current;
  if (!m.control_root) throw new Error("Only staff can write to the Control Plane");
  const controlSigner = await controlSignerKey(m.control_root, membership.communityId, m.root_epoch);
  const controlRead = await controlReadKey(m.community_root, membership.communityId, m.root_epoch);
  const tags = await nextEditionTags({ vsk, eid, heads });
  const { created_at, ms } = concordTime();
  const wrap = await createStreamEvent({
    stream: { ...controlSigner, convKey: controlRead.convKey },
    sealKind: KIND_SEAL_PLAINTEXT,
    authorPub,
    signer,
    rumor: {
      kind: KIND_EDITION,
      content: JSON.stringify(content),
      tags: [...tags, ["ms", ms]],
      created_at,
    },
  });
  await sink.event(wrap);
}

/** Create a new public channel (MANAGE_CHANNELS). */
export async function publishCreateChannel(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  name: string;
  heads: Map<string, Edition>;
  sink: EventSink;
}): Promise<string> {
  const channelId = bytesToHex(randomBytes(32));
  await publishEdition({
    ...opts,
    vsk: VSK.CHANNEL,
    eid: channelId,
    content: { name: opts.name.slice(0, 64), private: false },
  });
  return channelId;
}

/** Replace the banlist entire (CORD-04 §4). Silences instantly in every client. */
export async function publishBanlist(opts: {
  signer: GiftSigner;
  authorPub: string;
  membership: Membership;
  banned: string[];
  heads: Map<string, Edition>;
  sink: EventSink;
}): Promise<void> {
  const eid = await banlistLocator(opts.membership.communityId);
  await publishEdition({
    ...opts,
    vsk: VSK.BANLIST,
    eid,
    content: opts.banned,
  });
}
